import React, { useState, useEffect, useCallback } from 'react';
import { financeAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Trash2, Pencil, TrendingUp, TrendingDown, DollarSign, Wallet,
  ArrowUpCircle, ArrowDownCircle, Tag, ShoppingCart, Filter, X
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell
} from 'recharts';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#64748b'];

const MONTH_NAMES = [
  { value: '01', label: 'Январь' }, { value: '02', label: 'Февраль' },
  { value: '03', label: 'Март' }, { value: '04', label: 'Апрель' },
  { value: '05', label: 'Май' }, { value: '06', label: 'Июнь' },
  { value: '07', label: 'Июль' }, { value: '08', label: 'Август' },
  { value: '09', label: 'Сентябрь' }, { value: '10', label: 'Октябрь' },
  { value: '11', label: 'Ноябрь' }, { value: '12', label: 'Декабрь' },
];

export default function FinancePage() {
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Top-level period filter for summary & charts
  const [topPeriod, setTopPeriod] = useState('month'); // default to current month
  const [topMonth, setTopMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [topYear, setTopYear] = useState(() => String(new Date().getFullYear()));

  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [periodMode, setPeriodMode] = useState('month'); // default to current month
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));

  // Dialogs
  const [txnDialog, setTxnDialog] = useState(false);
  const [catDialog, setCatDialog] = useState(false);
  const [editingTxn, setEditingTxn] = useState(null);
  const [editingCat, setEditingCat] = useState(null);

  // Transaction form
  const [txnForm, setTxnForm] = useState({ type: 'expense', amount: '', category_id: '', description: '', date: new Date().toISOString().slice(0, 10) });
  // Category form
  const [catForm, setCatForm] = useState({ name: '', type: 'expense', color: '#6366f1' });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType !== 'all') params.type = filterType;
      if (filterCategory !== 'all') params.category_id = filterCategory;

      // Date filters based on period mode
      let summaryFrom = dateFrom;
      let summaryTo = dateTo;

      if (periodMode === 'month') {
        const [y, m] = selectedMonth.split('-');
        summaryFrom = `${y}-${m}-01`;
        const lastDay = new Date(Number(y), Number(m), 0).getDate();
        summaryTo = `${y}-${m}-${String(lastDay).padStart(2, '0')}`;
      } else if (periodMode === 'year') {
        summaryFrom = `${selectedYear}-01-01`;
        summaryTo = `${selectedYear}-12-31`;
      } else if (periodMode === 'custom') {
        summaryFrom = dateFrom;
        summaryTo = dateTo;
      } else {
        summaryFrom = '';
        summaryTo = '';
      }

      if (summaryFrom) params.date_from = summaryFrom;
      if (summaryTo) params.date_to = summaryTo;

      const summaryParams = {};
      // Top-level period filter for summary & charts
      if (topPeriod === 'month') {
        const [ty, tm] = topMonth.split('-');
        summaryParams.date_from = `${ty}-${tm}-01`;
        const lastDay = new Date(Number(ty), Number(tm), 0).getDate();
        summaryParams.date_to = `${ty}-${tm}-${String(lastDay).padStart(2, '0')}`;
      } else if (topPeriod === 'year') {
        summaryParams.date_from = `${topYear}-01-01`;
        summaryParams.date_to = `${topYear}-12-31`;
      }

      const [sumRes, chartRes, txnRes, catRes] = await Promise.all([
        financeAPI.getSummary(summaryParams),
        financeAPI.getChartData(),
        financeAPI.getTransactions(params),
        financeAPI.getCategories(),
      ]);
      setSummary(sumRes.data);
      setChartData(chartRes.data);
      setTransactions(txnRes.data);
      setCategories(catRes.data);
    } catch (e) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterCategory, dateFrom, dateTo, periodMode, selectedMonth, selectedYear, topPeriod, topMonth, topYear]);

  useEffect(() => { loadData(); }, [loadData]);

  const fmt = (v) => {
    if (v === null || v === undefined) return '0';
    return Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
  };

  // --- Transaction CRUD ---
  const openNewTxn = (type = 'expense') => {
    setEditingTxn(null);
    setTxnForm({ type, amount: '', category_id: '', description: '', date: new Date().toISOString().slice(0, 10) });
    setTxnDialog(true);
  };

  const openEditTxn = (t) => {
    setEditingTxn(t);
    setTxnForm({ type: t.type, amount: String(t.amount), category_id: t.category_id || '', description: t.description || '', date: t.date || '' });
    setTxnDialog(true);
  };

  const saveTxn = async () => {
    if (!txnForm.amount || isNaN(txnForm.amount) || Number(txnForm.amount) <= 0) {
      toast.error('Введите корректную сумму');
      return;
    }
    try {
      const payload = { ...txnForm, amount: Number(txnForm.amount), category_id: txnForm.category_id || null };
      if (editingTxn) {
        await financeAPI.updateTransaction(editingTxn.id, payload);
        toast.success('Транзакция обновлена');
      } else {
        await financeAPI.createTransaction(payload);
        toast.success('Транзакция добавлена');
      }
      setTxnDialog(false);
      loadData();
    } catch { toast.error('Ошибка сохранения'); }
  };

  const deleteTxn = async (id) => {
    try {
      await financeAPI.deleteTransaction(id);
      toast.success('Транзакция удалена');
      loadData();
    } catch { toast.error('Ошибка удаления'); }
  };

  // --- Category CRUD ---
  const openNewCat = () => {
    setEditingCat(null);
    setCatForm({ name: '', type: 'expense', color: COLORS[categories.length % COLORS.length] });
    setCatDialog(true);
  };

  const openEditCat = (c) => {
    setEditingCat(c);
    setCatForm({ name: c.name, type: c.type, color: c.color });
    setCatDialog(true);
  };

  const saveCat = async () => {
    if (!catForm.name.trim()) { toast.error('Введите название категории'); return; }
    try {
      if (editingCat) {
        await financeAPI.updateCategory(editingCat.id, catForm);
        toast.success('Категория обновлена');
      } else {
        await financeAPI.createCategory(catForm);
        toast.success('Категория создана');
      }
      setCatDialog(false);
      loadData();
    } catch { toast.error('Ошибка сохранения'); }
  };

  const deleteCat = async (id) => {
    if (!window.confirm('Удалить категорию? Транзакции с этой категорией останутся без категории.')) return;
    try {
      await financeAPI.deleteCategory(id);
      toast.success('Категория удалена');
      loadData();
    } catch { toast.error('Ошибка удаления'); }
  };

  // Helpers
  const getCatName = (catId) => {
    if (!catId) return 'Без категории';
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.name : 'Без категории';
  };

  const getCatColor = (catId) => {
    if (!catId) return '#94a3b8';
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.color : '#94a3b8';
  };

  // Pie chart data from category expenses
  const pieData = summary ? Object.entries(summary.category_expenses || {}).map(([catId, amount]) => ({
    name: getCatName(catId),
    value: amount,
    color: getCatColor(catId),
  })).filter(d => d.value > 0) : [];

  // Pie chart data from category incomes
  const pieIncomeData = summary ? Object.entries(summary.category_incomes || {}).map(([catId, amount]) => ({
    name: getCatName(catId),
    value: amount,
    color: getCatColor(catId),
  })).filter(d => d.value > 0) : [];

  const clearFilters = () => {
    setFilterType('all');
    setFilterCategory('all');
    setDateFrom('');
    setDateTo('');
    setPeriodMode('all');
  };

  const hasFilters = filterType !== 'all' || filterCategory !== 'all' || periodMode !== 'all';

  const V = (v) => loading ? '...' : fmt(v);

  return (
    <div className="flex flex-col gap-6" data-testid="finance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Финансы</h1>
          <p className="text-sm text-muted-foreground">Доходы, расходы и аналитика</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => openNewTxn('income')}>
            <ArrowUpCircle className="h-4 w-4 mr-1" />Доход
          </Button>
          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => openNewTxn('expense')}>
            <ArrowDownCircle className="h-4 w-4 mr-1" />Расход
          </Button>
          <Button size="sm" variant="outline" onClick={openNewCat}>
            <Plus className="h-4 w-4 mr-0.5" /><Tag className="h-4 w-4 mr-1" />Категория
          </Button>
        </div>
      </div>

      {/* Top period filter for summary & charts */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          { value: 'all', label: 'Всё время' },
          { value: 'month', label: 'Месяц' },
          { value: 'year', label: 'Год' },
        ].map(p => (
          <button key={p.value}
            onClick={() => setTopPeriod(p.value)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
              topPeriod === p.value
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted'
            }`}>
            {p.label}
          </button>
        ))}
        {topPeriod === 'month' && (
          <div className="flex items-center gap-1.5">
            <Select value={topMonth.split('-')[1]} onValueChange={m => setTopMonth(`${topMonth.split('-')[0]}-${m}`)}>
              <SelectTrigger className="w-[130px] h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTH_NAMES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={topMonth.split('-')[0]} onValueChange={y => setTopMonth(`${y}-${topMonth.split('-')[1]}`)}>
              <SelectTrigger className="w-[90px] h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 5 }, (_, i) => String(new Date().getFullYear() - i)).map(y => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {topPeriod === 'year' && (
          <Select value={topYear} onValueChange={setTopYear}>
            <SelectTrigger className="w-[100px] h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: 5 }, (_, i) => String(new Date().getFullYear() - i)).map(y => (
                <SelectItem key={y} value={y}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4 text-emerald-500" />Общий доход
            </div>
            <p className="text-2xl font-bold text-emerald-600">{V(summary?.total_income)} ₽</p>
            <p className="text-xs text-muted-foreground mt-1">Заказы: {V(summary?.orders_income)} ₽ • Ручные: {V(summary?.manual_income)} ₽</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <TrendingDown className="h-4 w-4 text-red-500" />Расходы
            </div>
            <p className="text-2xl font-bold text-red-600">{V(summary?.total_expense)} ₽</p>
            <p className="text-xs text-muted-foreground mt-1">{summary?.expense_count || 0} транзакций</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Wallet className="h-4 w-4 text-violet-500" />Прибыль
            </div>
            <p className={`text-2xl font-bold ${(summary?.profit || 0) >= 0 ? 'text-violet-600' : 'text-red-600'}`}>
              {V(summary?.profit)} ₽
            </p>
            <p className="text-xs text-muted-foreground mt-1">Доход {V(summary?.total_income)} ₽ − Расход {V(summary?.total_expense)} ₽</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <ShoppingCart className="h-4 w-4 text-blue-500" />Из заказов
            </div>
            <p className="text-2xl font-bold text-blue-600">{V(summary?.orders_income)} ₽</p>
            <p className="text-xs text-muted-foreground mt-1">{summary?.orders_count || 0} заказов • Ожидается: {V(summary?.orders_remaining)} ₽</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <DollarSign className="h-4 w-4 text-amber-500" />Суммы заказов
            </div>
            <p className="text-2xl font-bold text-amber-600">{V(summary?.orders_total)} ₽</p>
            <p className="text-xs text-muted-foreground mt-1">Оплачено: {summary?.orders_total ? Math.round(((summary?.orders_income || 0) / summary.orders_total) * 100) : 0}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts - Desktop: side by side, Mobile: separate blocks */}
      <div className="hidden lg:grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Доходы и расходы по месяцам</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tickFormatter={(v) => {
                    const [y, m] = v.split('-');
                    return new Date(Number(y), Number(m) - 1).toLocaleDateString('ru-RU', { month: 'short' });
                  }} fontSize={12} />
                  <YAxis fontSize={12} tickFormatter={(v) => v >= 1000 ? `${v / 1000}к` : v} />
                  <Tooltip formatter={(v) => `${Number(v).toLocaleString('ru-RU')} ₽`} labelFormatter={(v) => {
                    const [y, m] = v.split('-');
                    return new Date(Number(y), Number(m) - 1).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
                  }} />
                  <Legend />
                  <Bar dataKey="income" name="Доход" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expense" name="Расход" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground">Нет данных</div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Расходы по категориям</CardTitle>
            </CardHeader>
            <CardContent>
              {pieData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                        {pieData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                      </Pie>
                      <Tooltip formatter={(v) => `${Number(v).toLocaleString('ru-RU')} ₽`} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2 mt-3">
                    {pieData.map((d, i) => (
                      <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-border/50 last:border-0">
                        <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} /><span>{d.name}</span></div>
                        <span className="font-semibold">{fmt(d.value)} ₽</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (<div className="h-[180px] flex items-center justify-center text-muted-foreground text-sm">Нет данных</div>)}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Доходы по категориям</CardTitle>
            </CardHeader>
            <CardContent>
              {pieIncomeData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={pieIncomeData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                        {pieIncomeData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                      </Pie>
                      <Tooltip formatter={(v) => `${Number(v).toLocaleString('ru-RU')} ₽`} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2 mt-3">
                    {pieIncomeData.map((d, i) => (
                      <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-border/50 last:border-0">
                        <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} /><span>{d.name}</span></div>
                        <span className="font-semibold">{fmt(d.value)} ₽</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (<div className="h-[180px] flex items-center justify-center text-muted-foreground text-sm">Нет данных</div>)}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mobile: each chart as separate card */}
      <Card className="lg:hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Доходы и расходы по месяцам</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tickFormatter={(v) => {
                  const [y, m] = v.split('-');
                  return new Date(Number(y), Number(m) - 1).toLocaleDateString('ru-RU', { month: 'short' });
                }} fontSize={12} />
                <YAxis fontSize={12} tickFormatter={(v) => v >= 1000 ? `${v / 1000}к` : v} />
                <Tooltip formatter={(v) => `${Number(v).toLocaleString('ru-RU')} ₽`} labelFormatter={(v) => {
                  const [y, m] = v.split('-');
                  return new Date(Number(y), Number(m) - 1).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
                }} />
                <Legend />
                <Bar dataKey="income" name="Доход" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" name="Расход" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-muted-foreground">Нет данных</div>
          )}
        </CardContent>
      </Card>

      <Card className="lg:hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Расходы по категориям</CardTitle>
        </CardHeader>
        <CardContent>
          {pieData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                    {pieData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                  </Pie>
                  <Tooltip formatter={(v) => `${Number(v).toLocaleString('ru-RU')} ₽`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3 mt-4 pb-2">
                {pieData.map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-border/50 last:border-0">
                    <div className="flex items-center gap-2.5"><div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} /><span>{d.name}</span></div>
                    <span className="font-semibold">{fmt(d.value)} ₽</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground">Нет данных</div>
          )}
        </CardContent>
      </Card>

      <Card className="lg:hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Доходы по категориям</CardTitle>
        </CardHeader>
        <CardContent>
          {pieIncomeData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={pieIncomeData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                    {pieIncomeData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                  </Pie>
                  <Tooltip formatter={(v) => `${Number(v).toLocaleString('ru-RU')} ₽`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3 mt-4 pb-2">
                {pieIncomeData.map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-border/50 last:border-0">
                    <div className="flex items-center gap-2.5"><div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} /><span>{d.name}</span></div>
                    <span className="font-semibold">{fmt(d.value)} ₽</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground">Нет данных</div>
          )}
        </CardContent>
      </Card>

      {/* Categories Section */}
      {categories.length > 0 && (
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Tag className="h-3.5 w-3.5" />Категории
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 pt-0">
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <div key={cat.id} className="inline-flex items-center gap-1.5 bg-muted/50 rounded-full pl-2.5 pr-1 py-1 text-xs group">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                  <span className="font-medium">{cat.name}</span>
                  <span className="text-muted-foreground">({cat.type === 'income' ? 'доход' : cat.type === 'expense' ? 'расход' : 'всё'})</span>
                  <button onClick={() => openEditCat(cat)} className="text-muted-foreground hover:text-foreground p-1 sm:p-0.5 rounded-full hover:bg-muted transition-colors">
                    <Pencil className="h-4 w-4 sm:h-3 sm:w-3" />
                  </button>
                  <button onClick={() => deleteCat(cat.id)} className="text-muted-foreground hover:text-red-500 p-1 sm:p-0.5 rounded-full hover:bg-muted transition-colors">
                    <Trash2 className="h-4 w-4 sm:h-3 sm:w-3" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transactions Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Транзакции</CardTitle>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 px-2 text-xs">
                  <X className="h-3.5 w-3.5 mr-1" />Сбросить
                </Button>
              )}
            </div>

            {/* Period filter tabs */}
            <div className="flex flex-wrap gap-1.5">
              {[
                { value: 'all', label: 'Всё время' },
                { value: 'month', label: 'Месяц' },
                { value: 'year', label: 'Год' },
                { value: 'custom', label: 'Период' },
              ].map(p => (
                <button key={p.value}
                  onClick={() => setPeriodMode(p.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                    periodMode === p.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>

            {/* Period-specific selectors */}
            {periodMode === 'month' && (
              <div className="flex items-center gap-1.5">
                <Select value={selectedMonth.split('-')[1]} onValueChange={m => setSelectedMonth(`${selectedMonth.split('-')[0]}-${m}`)}>
                  <SelectTrigger className="w-[130px] h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTH_NAMES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={selectedMonth.split('-')[0]} onValueChange={y => setSelectedMonth(`${y}-${selectedMonth.split('-')[1]}`)}>
                  <SelectTrigger className="w-[90px] h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 5 }, (_, i) => String(new Date().getFullYear() - i)).map(y => (
                      <SelectItem key={y} value={y}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {periodMode === 'year' && (
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[120px] h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => String(new Date().getFullYear() - i)).map(y => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {periodMode === 'custom' && (
              <div className="flex items-center gap-2">
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 w-[140px] text-sm" />
                <span className="text-muted-foreground text-sm">—</span>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 w-[140px] text-sm" />
              </div>
            )}

            {/* Type & category filters */}
            <div className="flex flex-wrap items-center gap-2">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[130px] h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все типы</SelectItem>
                  <SelectItem value="income">Доходы</SelectItem>
                  <SelectItem value="expense">Расходы</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[160px] h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все категории</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {transactions.length > 0 ? (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 font-medium">Дата</th>
                      <th className="pb-2 font-medium">Тип</th>
                      <th className="pb-2 font-medium">Категория</th>
                      <th className="pb-2 font-medium">Описание</th>
                      <th className="pb-2 font-medium text-right">Сумма</th>
                      <th className="pb-2 font-medium text-right">Источник</th>
                      <th className="pb-2 w-16"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {transactions.map(t => (
                      <tr key={t.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                        <td className="py-2.5 whitespace-nowrap">
                          {t.date ? new Date(t.date).toLocaleDateString('ru-RU') : '—'}
                        </td>
                        <td className="py-2.5">
                          {t.type === 'income' ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                              <ArrowUpCircle className="h-3.5 w-3.5" />Доход
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                              <ArrowDownCircle className="h-3.5 w-3.5" />Расход
                            </span>
                          )}
                        </td>
                        <td className="py-2.5">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: getCatColor(t.category_id) }} />
                            {getCatName(t.category_id)}
                          </div>
                        </td>
                        <td className="py-2.5 max-w-[250px] truncate">{t.description || '—'}</td>
                        <td className={`py-2.5 text-right font-semibold ${t.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {t.type === 'income' ? '+' : '−'}{fmt(t.amount)} ₽
                        </td>
                        <td className="py-2.5 text-right text-muted-foreground text-xs">
                          {t.source === 'manual' ? 'Вручную' : 'Заказ'}
                        </td>
                        <td className="py-2.5 text-right">
                          {t.source === 'manual' && (
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => openEditTxn(t)} className="text-muted-foreground hover:text-foreground p-1">
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={() => deleteTxn(t.id)} className="text-muted-foreground hover:text-red-500 p-1">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden flex flex-col gap-4">
                {transactions.map(t => (
                  <div key={t.id} className="bg-muted/30 border border-border rounded-xl p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {t.type === 'income' ? (
                          <ArrowUpCircle className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <ArrowDownCircle className="h-4 w-4 text-red-500" />
                        )}
                        <span className={`text-lg font-bold ${t.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {t.type === 'income' ? '+' : '−'}{fmt(t.amount)} ₽
                        </span>
                      </div>
                      {t.source === 'manual' && (
                        <div className="flex items-center gap-2">
                          <button onClick={() => openEditTxn(t)} className="text-muted-foreground hover:text-foreground p-1.5">
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button onClick={() => deleteTxn(t.id)} className="text-muted-foreground hover:text-red-500 p-1.5">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    {t.description && (
                      <p className="text-sm">{t.description}</p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1 border-t border-border/30">
                      <span>{t.date ? new Date(t.date).toLocaleDateString('ru-RU') : '—'}</span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getCatColor(t.category_id) }} />
                        {getCatName(t.category_id)}
                      </span>
                      <span>{t.source === 'manual' ? 'Вручную' : 'Заказ'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Wallet className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>Нет транзакций</p>
              <p className="text-sm mt-1">Добавьте доход или расход для начала учёта</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction Dialog */}
      <Dialog open={txnDialog} onOpenChange={setTxnDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTxn ? 'Редактировать транзакцию' : 'Новая транзакция'}</DialogTitle>
            <DialogDescription>Заполните данные транзакции</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button variant={txnForm.type === 'income' ? 'default' : 'outline'} size="sm"
                className={txnForm.type === 'income' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                onClick={() => setTxnForm(f => ({ ...f, type: 'income' }))}>
                <ArrowUpCircle className="h-4 w-4 mr-1" />Доход
              </Button>
              <Button variant={txnForm.type === 'expense' ? 'default' : 'outline'} size="sm"
                className={txnForm.type === 'expense' ? 'bg-red-600 hover:bg-red-700' : ''}
                onClick={() => setTxnForm(f => ({ ...f, type: 'expense' }))}>
                <ArrowDownCircle className="h-4 w-4 mr-1" />Расход
              </Button>
            </div>
            <div>
              <Label>Сумма *</Label>
              <Input type="number" min="0" step="0.01" placeholder="0" value={txnForm.amount}
                onChange={e => setTxnForm(f => ({ ...f, amount: e.target.value }))} />
            </div>
            <div>
              <Label>Категория</Label>
              <Select value={txnForm.category_id || 'none'} onValueChange={v => setTxnForm(f => ({ ...f, category_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Без категории" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Без категории</SelectItem>
                  {categories.filter(c => c.type === 'both' || c.type === txnForm.type).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Описание</Label>
              <Input placeholder="Описание транзакции" value={txnForm.description}
                onChange={e => setTxnForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div>
              <Label>Дата</Label>
              <Input type="date" value={txnForm.date}
                onChange={e => setTxnForm(f => ({ ...f, date: e.target.value }))} />
            </div>
          </div>
          <DialogFooter className="flex flex-row gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setTxnDialog(false)}>Отмена</Button>
            <Button className="flex-1" onClick={saveTxn}>{editingTxn ? 'Сохранить' : 'Добавить'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCat ? 'Редактировать категорию' : 'Новая категория'}</DialogTitle>
            <DialogDescription>Настройте категорию для учёта</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Название *</Label>
              <Input placeholder="Например: Материалы" value={catForm.name}
                onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Label>Тип</Label>
              <Select value={catForm.type} onValueChange={v => setCatForm(f => ({ ...f, type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">Расход</SelectItem>
                  <SelectItem value="income">Доход</SelectItem>
                  <SelectItem value="both">Расход и доход</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Цвет</Label>
              <div className="flex gap-2 mt-1">
                {COLORS.map(c => (
                  <button key={c} className={`w-7 h-7 rounded-full border-2 transition-all ${catForm.color === c ? 'border-foreground scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: c }} onClick={() => setCatForm(f => ({ ...f, color: c }))} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="flex flex-row gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setCatDialog(false)}>Отмена</Button>
            {editingCat && (
              <Button variant="destructive" className="flex-1" onClick={() => { deleteCat(editingCat.id); setCatDialog(false); }}>
                <Trash2 className="h-4 w-4 mr-1" />Удалить
              </Button>
            )}
            <Button className="flex-1" onClick={saveCat}>{editingCat ? 'Сохранить' : 'Создать'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
